import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'

import { getCurrentIdToken } from '../services/account/authService'
import { getMyStats, type StatsResult } from '../services/account/statsApi'
import { getDeviceId } from '../utils/deviceUtils'
import { getCachedStats, setCachedStats } from '../utils/storage'
import { usePlayerAuth } from './PlayerAuthContext'
import { usePlayer } from './PlayerContext'

/**
 *  - `loading` nothing to show yet
 *  - `ready`   `data` is the server's latest answer (or today's cached copy while it refreshes)
 *  - `error`   the server could not be reached; `data` is null unless today's cached copy exists
 */
export type StatsStatus = 'loading' | 'ready' | 'error'

interface StatsContextValue {
  status: StatsStatus
  data: StatsResult | null
  /** Asks the server again. Call after finishing a game so the streak updates at once. */
  refresh: () => Promise<void>
}

const StatsContext = createContext<StatsContextValue | undefined>(undefined)

const todayUtc = () => new Date().toISOString().slice(0, 10)

/**
 * Streaks and stats, read from the server. The server counts the games it
 * recorded, so the numbers are the same on every device and there is nothing to
 * sync: when someone signs in and their browser is linked, the next read simply
 * covers every device on the account.
 */
export const StatsProvider = ({ children }: { children: ReactNode }) => {
  const { user } = usePlayerAuth()
  const { status: playerStatus } = usePlayer()
  const uid = user?.uid ?? null

  const [data, setData] = useState<StatsResult | null>(null)
  const [status, setStatus] = useState<StatsStatus>('loading')
  // Only the newest request may write its answer: a slow reply for a previous account must not win.
  const requestId = useRef(0)

  // A signed-in player's first read waits for the browser to be linked, so it already covers every device.
  const waitingForLink = uid !== null && playerStatus === 'syncing'

  const load = useCallback(async () => {
    const id = ++requestId.current
    try {
      const idToken = uid ? await getCurrentIdToken() : null
      const fresh = await getMyStats({ deviceId: getDeviceId(), idToken })
      if (id !== requestId.current) return
      setData(fresh)
      setStatus('ready')
      setCachedStats(fresh)
    } catch {
      if (id !== requestId.current) return
      // Keep what we already show; otherwise say so.
      setStatus((previous) => (previous === 'ready' ? 'ready' : 'error'))
    }
  }, [uid])

  // Show today's cached copy immediately, then replace it with the server's answer.
  useEffect(() => {
    const cached = getCachedStats()
    // A copy from another day would show yesterday's "played today", so it is ignored.
    if (cached && cached.today === todayUtc()) {
      setData((current) => current ?? cached)
      setStatus((current) => (current === 'loading' ? 'ready' : current))
    }
  }, [])

  useEffect(() => {
    if (waitingForLink) return
    void load()
  }, [load, waitingForLink])

  // Coming back to the tab after midnight UTC (or after playing elsewhere) must not show old numbers.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible' && !waitingForLink) void load()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [load, waitingForLink])

  const value = useMemo<StatsContextValue>(() => ({ status, data, refresh: load }), [status, data, load])

  return <StatsContext.Provider value={value}>{children}</StatsContext.Provider>
}

export const useStats = (): StatsContextValue => {
  const context = useContext(StatsContext)
  if (!context) {
    throw new Error('useStats must be used within a StatsProvider')
  }
  return context
}
